import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

/**
 * Light, dark, or follow the operating system.
 *
 * The theme is one attribute on <html>; every colour in the product is a token
 * that reads it. public/boot.js applies the stored preference before
 * first paint, and this module takes over once the app is running. The two
 * must agree on the storage key and on what "system" means.
 */

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "kb:theme";
const darkQuery = (): MediaQueryList => window.matchMedia("(prefers-color-scheme: dark)");

function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Storage blocked; fall through to the default.
  }
  return "system";
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") return darkQuery().matches ? "dark" : "light";
  return preference;
}

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
}

const themeStore = createStore<ThemeState>(() => ({
  preference: "system",
  resolved: "light",
}));

/**
 * Applies a theme in one frame. While the attribute changes, the
 * `theme-switching` class suspends transitions, so hundreds of elements snap
 * to the new colours together instead of each cross-fading on its own timer.
 */
function apply(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (root.dataset.theme === resolved) return;
  root.classList.add("theme-switching");
  root.dataset.theme = resolved;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.classList.remove("theme-switching");
    });
  });
}

export function setThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Not persisted; still applied for this session.
  }
  const resolved = resolveTheme(preference);
  themeStore.setState({ preference, resolved });
  apply(resolved);
}

/** Called once at startup. */
export function initTheme(): void {
  const preference = readPreference();
  const resolved = resolveTheme(preference);
  themeStore.setState({ preference, resolved });
  apply(resolved);

  // Follow the OS switching between light and dark, but only for "system".
  darkQuery().addEventListener("change", () => {
    const { preference: current } = themeStore.getState();
    if (current !== "system") return;
    const next = resolveTheme("system");
    themeStore.setState({ resolved: next });
    apply(next);
  });
}

export function useTheme(): ThemeState {
  return useStore(themeStore);
}
